import React, { useState, useEffect, useRef } from "react";
import DataTable from "react-data-table-component";

import "../css/PopupRegisterDeclaration.css";

import InputData from "../../components/InputData";
import Dropdown from "../../components/Dropdown";
import RadioButton from "../../components/RadioButton";
import ButtonIconH from "../../components/ButtonIconH";
import { post } from "../../js/post";
import { userData, inputOnEnter } from "../../js/utils";
import { MONTHS, LABOR_CONTROL_TYPES } from "../js/constants";

const TIPO_HORAS = 0;
const TIPO_DIAS = 1;

const TIPO_HORAS_OPTIONS = LABOR_CONTROL_TYPES.filter((t) =>
  ["JornadaLaboral", "Ausencia"].includes(t.id),
);
const TIPO_DIAS_OPTIONS = LABOR_CONTROL_TYPES.filter((t) =>
  [
    "AsuntosPropios",
    "Ausencia",
    "BajaLaboral",
    "Festivo",
    "Vacaciones",
  ].includes(t.id),
);

function getMonthName(monthNumber) {
  return MONTHS.find((m) => m.id === monthNumber)?.text || "";
}

function formatFechaHoras(fecha) {
  const [year, month, day] = fecha.split("-").map(Number);
  return `${day} DE ${getMonthName(month)} ${year}`;
}

function formatFechaDias(fechaDesde, fechaHasta) {
  const [yearD, monthD, dayD] = fechaDesde.split("-").map(Number);
  const [yearH, monthH, dayH] = fechaHasta.split("-").map(Number);

  if (yearD === yearH && monthD === monthH && dayD === dayH) {
    return `${dayD} DE ${getMonthName(monthD)} ${yearD}`;
  }

  if (yearD === yearH) {
    if (monthD === monthH) {
      return `${dayD} - ${dayH} DE ${getMonthName(monthD)} ${yearD}`;
    }
    return `${dayD} DE ${getMonthName(monthD)} - ${dayH} DE ${getMonthName(
      monthH,
    )} ${yearD}`;
  }

  return `${dayD} DE ${getMonthName(monthD)} ${yearD} - ${dayH} DE ${getMonthName(
    monthH,
  )} ${yearH}`;
}

function getMinutesFromTime(timeStr) {
  if (!timeStr) {
    return null;
  }

  const [h, m] = timeStr.split(":").map(Number);
  return h * 60 + m;
}

function getDiasInclusive(fechaDesde, fechaHasta) {
  const desde = new Date(fechaDesde);
  const hasta = new Date(fechaHasta);
  return Math.round((hasta - desde) / (24 * 60 * 60 * 1000)) + 1;
}

function getHorasColumnText(row) {
  if (row.tipoRegistro === "Diario") {
    return `${getDiasInclusive(row.fechaOrden, row.fechaHastaOrden)} DÍAS`;
  }

  const inicioMinutos = getMinutesFromTime(row.inicio);
  const finMinutos = getMinutesFromTime(row.fin);
  if (inicioMinutos == null || finMinutos == null) {
    return "";
  }

  const minutos = finMinutos - inicioMinutos;
  return `${Math.floor(minutos / 60)} HORAS Y ${minutos % 60} MINUTOS`;
}

export default ({
  show,
  agente,
  idAgente,
  idEmpresa,
  onCancel,
  onAccept,
  createPopupQuestion,
  removePopupQuestion,
}) => {
  const [tipoDeclaracion, setTipoDeclaracion] = useState(TIPO_HORAS);

  const [fechaHoras, setFechaHoras] = useState("");
  const [tipoHoras, setTipoHoras] = useState(TIPO_HORAS_OPTIONS[0].id);
  const [horaInicio, setHoraInicio] = useState("");
  const [horaFin, setHoraFin] = useState("");

  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [tipoDias, setTipoDias] = useState(TIPO_DIAS_OPTIONS[0].id);

  const [registrosHorarios, setRegistrosHorarios] = useState([]);
  const [registrosDiarios, setRegistrosDiarios] = useState([]);
  const [sendingRegistros, setSendingRegistros] = useState(false);
  const nextIdRef = useRef(0);

  const cancel = () => {
    onCancel();
  };

  const declararRegistros = async () => {
    if (registros.length === 0) {
      createPopupQuestion(
        "Debe añadir al menos un registro para enviar solicitud.",
        removePopupQuestion,
        null,
        "Entendido",
      );
      return;
    }

    setSendingRegistros(true);

    await post("RegistrarJornadas.php", {
      token: userData.token,
      idAgente,
      idEmpresa,
      registrosHorarios: registrosHorarios.map((r) => ({
        fecha: r.fechaOrden,
        horaInicio: `${r.inicio}:00`,
        horaFin: `${r.fin}:00`,
        tipo: r.tipoId,
      })),
      registrosDiarios: registrosDiarios.map((r) => ({
        fechaDesde: r.fechaOrden,
        fechaHasta: r.fechaHastaOrden,
        tipo: r.tipoId,
      })),
    });

    setSendingRegistros(false);

    createPopupQuestion(
      `Solicitud de ${registros.length} registros enviados correctamente.`,
      () => {
        removePopupQuestion();
        onCancel();
        onAccept();
      },
      null,
      "Entendido",
    );
  };

  const addHoras = () => {
    if (!fechaHoras) {
      createPopupQuestion(
        "Debe introducir una fecha correcta.",
        removePopupQuestion,
        null,
        "Entendido",
      );
      return;
    }

    if (!horaInicio || !horaFin) {
      createPopupQuestion(
        "Debe introducir una hora correcta.",
        removePopupQuestion,
        null,
        "Entendido",
      );
      return;
    }

    if (horaFin < horaInicio) {
      createPopupQuestion(
        "La hora de fin debe ser mayor a la hora de inicio.",
        removePopupQuestion,
        null,
        "Entendido",
      );
      return;
    }

    nextIdRef.current += 1;
    setRegistrosHorarios((prev) => [
      ...prev,
      {
        id: nextIdRef.current,
        tipoRegistro: "Horario",
        fechaOrden: fechaHoras,
        fecha: formatFechaHoras(fechaHoras),
        agente,
        inicio: horaInicio,
        fin: horaFin,
        tipoId: tipoHoras,
        tipo: TIPO_HORAS_OPTIONS.find((t) => t.id === tipoHoras)?.text || "",
      },
    ]);

    setFechaHoras("");
    setTipoHoras(TIPO_HORAS_OPTIONS[0].id);
    setHoraInicio("");
    setHoraFin("");
  };

  const addDias = () => {
    if (!fechaDesde || !fechaHasta) {
      createPopupQuestion(
        "Debe introducir una fecha correcta.",
        removePopupQuestion,
        null,
        "Entendido",
      );
      return;
    }

    if (fechaHasta < fechaDesde) {
      createPopupQuestion(
        "La fecha de fin debe ser mayor a la fecha de inicio.",
        removePopupQuestion,
        null,
        "Entendido",
      );
      return;
    }

    nextIdRef.current += 1;
    setRegistrosDiarios((prev) => [
      ...prev,
      {
        id: nextIdRef.current,
        tipoRegistro: "Diario",
        fechaOrden: fechaDesde,
        fechaHastaOrden: fechaHasta,
        fecha: formatFechaDias(fechaDesde, fechaHasta),
        agente,
        inicio: "",
        fin: "",
        tipoId: tipoDias,
        tipo: TIPO_DIAS_OPTIONS.find((t) => t.id === tipoDias)?.text || "",
      },
    ]);

    setFechaDesde("");
    setFechaHasta("");
    setTipoDias(TIPO_DIAS_OPTIONS[0].id);
  };

  const removeRegistro = (id, tipoRegistro) => {
    if (tipoRegistro === "Horario") {
      setRegistrosHorarios((prev) => prev.filter((r) => r.id !== id));
    } else {
      setRegistrosDiarios((prev) => prev.filter((r) => r.id !== id));
    }
  };

  const registros = [...registrosHorarios, ...registrosDiarios].sort(
    (a, b) => new Date(b.fechaOrden) - new Date(a.fechaOrden),
  );

  const registrosColumns = [
    { name: "Fecha", selector: (row) => row.fecha, width: "19%" },
    { name: "Agente", selector: (row) => row.agente, width: "22%" },
    {
      name: "Inicio",
      selector: (row) => row.inicio,
      center: true,
      width: "10%",
    },
    { name: "Fin", selector: (row) => row.fin, center: true, width: "10%" },
    { name: "Tipo", selector: (row) => row.tipo, width: "13%" },
    {
      name: "Horas",
      selector: (row) => getHorasColumnText(row),
      width: "17%",
    },
    {
      name: "",
      width: "9%",
      center: true,
      cell: (row) => (
        <div className="navtable-opt ntc-trash cash-voucher-popup">
          <img
            src="/images/remove-blue.svg"
            className="c-pointer"
            onClick={() => removeRegistro(row.id, row.tipoRegistro)}
          />
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (show) {
      setTipoDeclaracion(TIPO_HORAS);
      setFechaHoras("");
      setTipoHoras(TIPO_HORAS_OPTIONS[0].id);
      setHoraInicio("");
      setHoraFin("");
      setFechaDesde("");
      setFechaHasta("");
      setTipoDias(TIPO_DIAS_OPTIONS[0].id);
      setRegistrosHorarios([]);
      setRegistrosDiarios([]);
    }
  }, [show]);

  return show ? (
    <div className="popup-container popup-normal popup-registerdeclaration">
      <div className="popup-message">
        <div className="popup-header">
          <div className="popup-title">Declarar registro de jornada</div>
          <img
            src="/images/close-white.svg"
            loading="lazy"
            alt=""
            className="popup-close"
            onClick={cancel}
          />
        </div>
        <div className="popup-body">
          <div className="popup-body-registerdeclaration">
            <div className="popup-registerdeclaration-contenido-element">
              <div className="label registerdeclaration-popup-label">
                Agente
              </div>
              <span>{agente}</span>
            </div>

            <div className="registerdeclaration-sections-row">
              <div className="dashboard-sec-row">
                <div className="registerdeclaration-radio-cell">
                  <RadioButton
                    active={tipoDeclaracion === TIPO_HORAS}
                    onClick={() => setTipoDeclaracion(TIPO_HORAS)}
                  />
                </div>
                <div
                  className={`dashboard-section ${
                    tipoDeclaracion === TIPO_HORAS
                      ? "rdbtn-selectable-on"
                      : "rdbtn-selectable-off"
                  }`}
                >
                  <div className="dashboard-section-title">
                    <div className="opt-table-title opt-table-title-dash-stats">
                      Declarar registro de X horas
                    </div>
                  </div>
                  <div className="popup-registerdeclaration-section-body">
                    <div className="popup-registerdeclaration-contenido-element">
                      <div className="label registerdeclaration-popup-label">
                        Fecha
                      </div>
                      <InputData
                        type="date"
                        max="9999-12-31"
                        size={0}
                        tabIndex={1}
                        inactive={tipoDeclaracion !== TIPO_HORAS}
                        input={fechaHoras}
                        onChange={setFechaHoras}
                      />
                    </div>
                    <div className="popup-registerdeclaration-contenido-element">
                      <div className="label registerdeclaration-popup-label">
                        Tipo
                      </div>
                      <Dropdown
                        size={0}
                        tabIndex={2}
                        disabled={tipoDeclaracion !== TIPO_HORAS}
                        optionList={TIPO_HORAS_OPTIONS}
                        op={tipoHoras}
                        onChange={(e) => setTipoHoras(e.id)}
                      />
                    </div>
                    <div className="popup-registerdeclaration-contenido-element">
                      <div className="label registerdeclaration-popup-label"></div>
                      <input
                        type="time"
                        className="input-field registerdeclaration-time-input"
                        tabIndex={3}
                        disabled={tipoDeclaracion !== TIPO_HORAS}
                        value={horaInicio}
                        onChange={(e) => setHoraInicio(e.target.value)}
                        onKeyUp={(e) => e.key === "Enter" && inputOnEnter(e)}
                      />
                      <span className="registerdeclaration-time-separator">
                        a
                      </span>
                      <input
                        type="time"
                        className="input-field registerdeclaration-time-input"
                        tabIndex={4}
                        disabled={tipoDeclaracion !== TIPO_HORAS}
                        value={horaFin}
                        onChange={(e) => setHoraFin(e.target.value)}
                        onKeyUp={(e) => e.key === "Enter" && inputOnEnter(e)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="dashboard-sec-row">
                <div className="registerdeclaration-radio-cell">
                  <RadioButton
                    active={tipoDeclaracion === TIPO_DIAS}
                    onClick={() => setTipoDeclaracion(TIPO_DIAS)}
                  />
                </div>
                <div
                  className={`dashboard-section ${
                    tipoDeclaracion === TIPO_DIAS
                      ? "rdbtn-selectable-on"
                      : "rdbtn-selectable-off"
                  }`}
                >
                  <div className="dashboard-section-title">
                    <div className="opt-table-title opt-table-title-dash-stats">
                      Declarar registro de X días
                    </div>
                  </div>
                  <div className="popup-registerdeclaration-section-body">
                    <div className="popup-registerdeclaration-contenido-element">
                      <div className="label registerdeclaration-popup-label">
                        Fecha desde
                      </div>
                      <InputData
                        type="date"
                        max="9999-12-31"
                        size={0}
                        tabIndex={5}
                        inactive={tipoDeclaracion !== TIPO_DIAS}
                        input={fechaDesde}
                        onChange={setFechaDesde}
                      />
                    </div>
                    <div className="popup-registerdeclaration-contenido-element">
                      <div className="label registerdeclaration-popup-label">
                        Fecha hasta
                      </div>
                      <InputData
                        type="date"
                        max="9999-12-31"
                        size={0}
                        tabIndex={6}
                        inactive={tipoDeclaracion !== TIPO_DIAS}
                        input={fechaHasta}
                        onChange={setFechaHasta}
                      />
                    </div>
                    <div className="popup-registerdeclaration-contenido-element">
                      <div className="label registerdeclaration-popup-label">
                        Tipo
                      </div>
                      <Dropdown
                        size={0}
                        tabIndex={7}
                        disabled={tipoDeclaracion !== TIPO_DIAS}
                        optionList={TIPO_DIAS_OPTIONS}
                        op={tipoDias}
                        onChange={(e) => setTipoDias(e.id)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="registerdeclaration-add-button-row">
              <ButtonIconH
                text={
                  tipoDeclaracion === TIPO_HORAS
                    ? "Añadir horas"
                    : "Añadir días"
                }
                small={true}
                onClick={tipoDeclaracion === TIPO_HORAS ? addHoras : addDias}
              />
            </div>

            <div className="popup-registerdeclaration-section-title">
              Listado de registros
            </div>
            <DataTable
              columns={registrosColumns}
              data={registros}
              className="custom-datatable"
              noDataComponent="No hay registros añadidos."
            />
          </div>
        </div>
        <div className="popup-footer">
          <div className="popup-row-buttons">
            <ButtonIconH
              text="Declarar registros"
              disabled={sendingRegistros}
              onClick={declararRegistros}
            />
            <ButtonIconH
              text="Cancelar"
              disabled={sendingRegistros}
              onClick={cancel}
            />
          </div>
        </div>
      </div>
    </div>
  ) : null;
};
